import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { PackageCheck, Plus, FileSignature, Download } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Spinner } from "@/components/tac";

const inp = "w-full rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none";

function SigPad({ onCapture }) {
  const ref = useRef(null); const drawing = useRef(false); const len = useRef(0);
  const init = (c) => { if (!c) return; ref.current = c; const x = c.getContext("2d"); x.fillStyle = "#0d1420"; x.fillRect(0, 0, c.width, c.height); x.strokeStyle = "#F59E0B"; x.lineWidth = 2.5; x.lineCap = "round"; };
  const pos = (e) => { const r = ref.current.getBoundingClientRect(); const t = e.touches?.[0]; return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top }; };
  return (
    <div>
      <canvas ref={init} width={320} height={90} data-testid="handover-sig-canvas" className="w-full touch-none rounded-lg border border-slate-700"
        onPointerDown={(e) => { drawing.current = true; const c = ref.current.getContext("2d"); const { x, y } = pos(e); c.beginPath(); c.moveTo(x, y); }}
        onPointerMove={(e) => { if (!drawing.current) return; e.preventDefault(); const c = ref.current.getContext("2d"); const { x, y } = pos(e); c.lineTo(x, y); c.stroke(); len.current += 1; }}
        onPointerUp={() => { drawing.current = false; onCapture(len.current >= 8 ? ref.current.toDataURL("image/png") : ""); }} />
      <button className="mt-1 text-[10px] text-slate-500 hover:text-white" onClick={() => { init(ref.current); len.current = 0; onCapture(""); }}>Clear signature</button>
    </div>
  );
}

export default function ProjectHandover({ projectId, canEdit, onChange }) {
  const qc = useQueryClient();
  const { data: assets, isLoading } = useQuery({ queryKey: ["assets", projectId], queryFn: async () => (await api.get(`/projects/${projectId}/assets`)).data });
  const [form, setForm] = useState({ name: "", category: "", serial: "", model: "", location: "", load_balance: "", startup_metrics: "", warranty_months: 12 });
  const [ho, setHo] = useState({ signer_name: "", signature: "", notes: "" });
  const refresh = () => qc.invalidateQueries({ queryKey: ["assets", projectId] });

  const addAsset = async () => {
    try { await api.post(`/projects/${projectId}/assets`, form); toast.success("Asset logged"); setForm({ name: "", category: "", serial: "", model: "", location: "", load_balance: "", startup_metrics: "", warranty_months: 12 }); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const packet = (warranty, qbo) => {
    const doc = new jsPDF({ unit: "pt", format: "letter" }); let y = 56;
    doc.setFontSize(18); doc.text("Warranty & Handover Packet", 48, y); y += 24;
    doc.setFontSize(10); doc.setTextColor(90); doc.text(`Final invoice ref: ${qbo}`, 48, y); y += 24;
    doc.setTextColor(20); doc.setFontSize(13); doc.text("Installed equipment & warranties", 48, y); y += 18;
    doc.setFontSize(10); doc.setTextColor(60);
    (warranty || []).forEach((w) => { doc.text(`- ${w.name}  serial ${w.serial || "n/a"}  warranty ${w.warranty_months} mo`, 56, y); y += 16; });
    doc.save(`Warranty_Packet_${projectId.slice(0, 8)}.pdf`);
  };

  const handover = async () => {
    if (!ho.signer_name.trim()) return toast.error("Enter signer name");
    if (!ho.signature) return toast.error("Capture a signature");
    try { const { data } = await api.post(`/projects/${projectId}/handover`, ho);
      toast.success(`Handover complete · invoice ${data.qbo_ref}`);
      packet(data.warranty_items, data.qbo_ref); onChange?.();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (isLoading) return <div className="mt-8"><Spinner label="Loading assets" /></div>;
  return (
    <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_340px]" data-testid="handover-tab">
      {/* Assets / commissioning */}
      <div>
        <h2 className="mb-3 flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500"><PackageCheck className="h-4 w-4 text-emerald-400" /> Commissioned equipment</h2>
        {canEdit && (
          <div className="card-tac mb-4 grid grid-cols-2 gap-2 p-3">
            <input placeholder="Equipment name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} data-testid="asset-name-input" />
            <input placeholder="Category (furnace, panel…)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inp} data-testid="asset-category-input" />
            <input placeholder="Serial #" value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} className={inp} data-testid="asset-serial-input" />
            <input placeholder="Model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className={inp} />
            <input placeholder="Load balance result" value={form.load_balance} onChange={(e) => setForm({ ...form, load_balance: e.target.value })} className={inp} />
            <input placeholder="Startup metrics" value={form.startup_metrics} onChange={(e) => setForm({ ...form, startup_metrics: e.target.value })} className={inp} />
            <input type="number" placeholder="Warranty months" value={form.warranty_months} onChange={(e) => setForm({ ...form, warranty_months: Number(e.target.value) })} className={inp} />
            <button disabled={!form.name.trim()} onClick={addAsset} className="btn-amber inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-xs disabled:opacity-50" data-testid="add-asset-btn"><Plus className="h-3.5 w-3.5" /> Log asset</button>
          </div>
        )}
        <div className="space-y-2">
          {(assets || []).map((a) => (
            <div key={a.id} className="card-tac flex items-center justify-between p-3" data-testid={`asset-${a.id}`}>
              <div><div className="text-sm text-white">{a.name} <span className="font-mono-tac text-[10px] text-slate-500">{a.category}</span></div>
                <div className="font-mono-tac text-[10px] text-slate-500">SN {a.serial || "n/a"} · {a.model || "-"} · {a.warranty_months}mo warranty</div></div>
            </div>
          ))}
          {!(assets || []).length && <div className="card-tac py-10 text-center text-sm text-slate-500" data-testid="assets-empty">No equipment logged yet.</div>}
        </div>
      </div>

      {/* Handover sign-off */}
      <div>
        <h2 className="mb-3 flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500"><FileSignature className="h-4 w-4 text-amber-400" /> Digital handover</h2>
        <div className="card-tac space-y-3 p-4" data-testid="handover-panel">
          <p className="text-xs text-slate-400">Customer / GC final walkthrough sign-off. Generates the warranty packet and syncs the final invoice.</p>
          <input placeholder="Signer name (customer / GC)" value={ho.signer_name} onChange={(e) => setHo({ ...ho, signer_name: e.target.value })} className={inp} data-testid="handover-signer-input" />
          <SigPad onCapture={(s) => setHo((h) => ({ ...h, signature: s }))} />
          <textarea placeholder="Notes (optional)" rows={2} value={ho.notes} onChange={(e) => setHo({ ...ho, notes: e.target.value })} className={inp} />
          {canEdit ? (
            <button onClick={handover} className="btn-amber w-full rounded-xl py-3 text-sm" data-testid="handover-submit"><Download className="mr-1 inline h-4 w-4" /> Sign off & generate packet</button>
          ) : <p className="text-xs text-slate-600">Owner/dispatcher completes handover.</p>}
        </div>
      </div>
    </div>
  );
}
